import { useMemo, useRef, useState } from 'react'

import ClientSideTable from '@/components/Table/ClientSideTable'
import useGCMaterial from '@/features/GCMaterials/useGCMaterials'
import { Currency } from '@/features/offers/constants'
import { selectOfferDetail } from '@/features/offers/slices/offerDetail.slice'
import { excelFileTypes } from '@/utils/constants'
import { getExchangeRateToHKD, HKD_CURRENCY, useExchangeRate } from '@/utils/exchangeRate.helper'
import { createExcelWithOptions, readExcelFile } from '@/utils/fileHelper'
import { numberWithCommas } from '@/utils/stringHelper'
import { Button, Flex, Spacer, Text } from '@chakra-ui/react'
import { RoleInOffer } from '@eapproval/offer-shared'
import { saveAs } from 'file-saver'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { ACCESSOR_TO_HEADER, useGCMaterialProjectExecutionColumns } from './GCMaterialProjectExecutionTable.columns'
import { calculateGCPRSubmissionTotalAmount, getGCMaterialCurrency, getGCPRRowUnitCost } from '../utils/GCPR.helper'

const HEADER_TO_ACCESSOR = {
  [ACCESSOR_TO_HEADER.ref]: 'ref',
  [ACCESSOR_TO_HEADER.subSystemIndex]: 'subSystemIndex',
  [ACCESSOR_TO_HEADER.modelNumber]: 'modelNumber',
  [ACCESSOR_TO_HEADER.description]: 'description',
  [ACCESSOR_TO_HEADER.masterDescription]: 'masterDescription',
  [ACCESSOR_TO_HEADER.quantity]: 'quantity',
  [ACCESSOR_TO_HEADER.currency]: 'currency',
  [ACCESSOR_TO_HEADER.manualUnitCost]: 'manualUnitCost',
  [ACCESSOR_TO_HEADER.masterUnitCost]: 'masterUnitCost',
  [ACCESSOR_TO_HEADER.totalRequestedQuantity]: 'totalRequestedQuantity',
  [ACCESSOR_TO_HEADER.totalRequestedCost]: 'totalRequestedCost',
  [ACCESSOR_TO_HEADER.newRequestedQuantity]: 'newRequestedQuantity',
  [ACCESSOR_TO_HEADER.newRequestedCost]: 'newRequestedCost',
}

const OfferGCMaterialProjectExecutionTable = ({ isFreezed = false, disabled = false, subSystemFilter = null }) => {
  const { role, adminMode } = useSelector(selectOfferDetail)
  const [jumpToLastPageTrigger, setJumpToLastPageTrigger] = useState(0)
  const importExcelRef = useRef(null)

  const { useSearchGCMaterialsByModelNumber } = useGCMaterial()
  const { mutateAsync: searchGCMaterialsByModelNumber } = useSearchGCMaterialsByModelNumber()
  const { getValues, setValue } = useFormContext()

  const watchedProjectExecutionGCMaterialDetails = useWatch({ name: `sourcing.projectExecutionGCMaterialDetails` })
  const projectExecutionGCMaterialDetails = useMemo(
    () => watchedProjectExecutionGCMaterialDetails ?? [],
    [watchedProjectExecutionGCMaterialDetails]
  )
  const watchedSubSystems = useWatch({ name: 'subSystems' })
  const subSystems = useMemo(() => watchedSubSystems ?? [], [watchedSubSystems])
  const { append: appendGCMaterial, remove: removeGCMaterial } = useFieldArray({
    name: `sourcing.projectExecutionGCMaterialDetails`,
  })
  const { data: exchangeRateMap } = useExchangeRate()

  const isProjectManager = role === RoleInOffer.MANAGER || role === RoleInOffer.BOOKING_MANAGER
  const subSystemOptions = useMemo(() => {
    if (!Array.isArray(subSystems)) return []

    return subSystems.map((subSystem, index) => ({
      label: `1.${index + 1} - ${subSystem.projectName}`,
      value: index,
    }))
  }, [subSystems])

  const currencyOptions = useMemo(
    () =>
      Object.values(Currency).map((currency) => ({
        label: currency,
        value: currency,
      })),
    []
  )

  const projectExecutionData = useMemo(() => {
    if (!Array.isArray(projectExecutionGCMaterialDetails)) return []
    return projectExecutionGCMaterialDetails
      .map((material, index) => ({
        ...material,
        originalIndex: index,
      }))
      .filter((material) => subSystemFilter === null || material.subSystemIndex === subSystemFilter)
  }, [projectExecutionGCMaterialDetails, subSystemFilter])

  const pm100TotalCost = useMemo(() => {
    if (!Array.isArray(subSystems)) return 0
    return subSystems.reduce((sum, subSystem, index) => {
      if (subSystemFilter !== null && index !== subSystemFilter) return sum
      if (!Array.isArray(subSystem.PM100GCMaterials)) return sum
      return (
        sum +
        subSystem.PM100GCMaterials.reduce((subSum, material) => {
          return subSum + parseFloat(material?.totalHKDCost)
        }, 0)
      )
    }, 0)
  }, [subSystems, subSystemFilter])

  const currentCost = useMemo(() => {
    if (!Array.isArray(projectExecutionGCMaterialDetails)) return 0
    return projectExecutionGCMaterialDetails.reduce((sum, row) => {
      if (subSystemFilter !== null && row?.subSystemIndex !== subSystemFilter) return sum
      return sum + parseFloat(row?.totalRequestedCost ?? 0)
    }, 0)
  }, [projectExecutionGCMaterialDetails, subSystemFilter])

  const getNewRequestedCostHKD = (row) => {
    const unitCost = getGCPRRowUnitCost(row, exchangeRateMap)
    const quantity = row?.newRequestedQuantity ?? 0
    const totalHKD = unitCost * quantity
    return totalHKD
  }

  const columnDef = useGCMaterialProjectExecutionColumns({
    currencyOptions,
    subSystemFilter,
    removeGCMaterial,
    getNewRequestedCostHKD,
    adminMode,
  })

  const formatData = (data) => {
    return data.map((item, index) => {
      const subSystemOption = subSystemOptions.find((option) => `${option.value}` === `${item.subSystemIndex}`)
      const masterDetail = item?.detail

      const masterUnitCost = masterDetail?.unitCost ?? 0
      const masterDescription = masterDetail?.description ?? ''
      return {
        [ACCESSOR_TO_HEADER.ref]: index + 1,
        [ACCESSOR_TO_HEADER.subSystemIndex]:
          subSystemOption?.label || (`${item.subSystemIndex}` !== '-1' ? `1.${parseInt(item.subSystemIndex) + 1}` : ''),
        [ACCESSOR_TO_HEADER.modelNumber]: item.modelNumber,
        [ACCESSOR_TO_HEADER.description]: item.description,
        [ACCESSOR_TO_HEADER.masterDescription]:
          masterDescription && masterDescription !== '' ? masterDescription : '(-)',
        [ACCESSOR_TO_HEADER.quantity]: item.quantity,
        [ACCESSOR_TO_HEADER.currency]: item.currency,
        [ACCESSOR_TO_HEADER.manualUnitCost]: `${numberWithCommas(item.manualUnitCost)}`,
        [ACCESSOR_TO_HEADER.masterUnitCost]: `${numberWithCommas(masterUnitCost)}`,
        [ACCESSOR_TO_HEADER.totalRequestedQuantity]: item.totalRequestedQuantity ?? 0,
        [ACCESSOR_TO_HEADER.totalRequestedCost]: item.totalRequestedCost ?? 0,
        [ACCESSOR_TO_HEADER.newRequestedQuantity]: item.newRequestedQuantity ?? 0,
        [ACCESSOR_TO_HEADER.newRequestedCost]: getNewRequestedCostHKD(item),
      }
    })
  }

  const handleUpdateTableData = async (rowIndex, columnId, newValue) => {
    // rowIndex is the index in the filtered data (projectExecutionData), not the original array
    const filteredRow = projectExecutionData[rowIndex]
    const originalIndex = filteredRow?.originalIndex
    if (originalIndex === undefined) {
      console.error('Could not find originalIndex for rowIndex', rowIndex)
      return
    }

    setValue(`sourcing.projectExecutionGCMaterialDetails.${originalIndex}.${columnId}`, newValue, { shouldDirty: true })

    if (columnId === 'modelNumber') {
      //Check if there is matching master data
      const modelSearchResult = await searchGCMaterialsByModelNumber(newValue)

      const { mappedDetail, allDetails } = modelSearchResult

      if (mappedDetail) {
        setValue(`sourcing.projectExecutionGCMaterialDetails.${originalIndex}.detail`, mappedDetail, {
          shouldDirty: true,
        })
        setValue(`sourcing.projectExecutionGCMaterialDetails.${originalIndex}.allDetails`, allDetails, {
          shouldDirty: true,
        })
      } else {
        setValue(`sourcing.projectExecutionGCMaterialDetails.${originalIndex}.detail`, undefined, {
          shouldDirty: true,
        })
        setValue(`sourcing.projectExecutionGCMaterialDetails.${originalIndex}.allDetails`, [], {
          shouldDirty: true,
        })
      }
    } else if (columnId === 'currency') {
      //Update manualUnitCostHKD
      const newCurrency = newValue
      const manualUnitCost =
        getValues(`sourcing.projectExecutionGCMaterialDetails.${originalIndex}.manualUnitCost`) ?? 0

      const exchangeRate = getExchangeRateToHKD(newCurrency, exchangeRateMap)
      const newManualUnitCostHKD = manualUnitCost * exchangeRate

      setValue(`sourcing.projectExecutionGCMaterialDetails.${originalIndex}.manualUnitCostHKD`, newManualUnitCostHKD, {
        shouldDirty: true,
      })
    } else if (columnId === 'manualUnitCost') {
      //Update manualUnitCostHKD
      const newManualUnitCost = newValue

      const currency = getValues(`sourcing.projectExecutionGCMaterialDetails.${originalIndex}.currency`) ?? 'HKD'
      const exchangeRate = getExchangeRateToHKD(currency, exchangeRateMap)
      const newManualUnitCostHKD = newManualUnitCost * exchangeRate

      setValue(`sourcing.projectExecutionGCMaterialDetails.${originalIndex}.manualUnitCostHKD`, newManualUnitCostHKD, {
        shouldDirty: true,
      })
    }
  }

  const handleAddItem = () => {
    appendGCMaterial({
      subSystemIndex: subSystemFilter === null ? -1 : subSystemFilter,
      modelNumber: '',
      description: '',
      SAPMaterialNumber: '',
      quantity: 0,
      unit: '',
      currency: 'HKD',
      manualUnitCost: 0,
      manualUnitCostHKD: 0,
      totalPrice: 0,
      totalRequestedQuantity: 0,
      totalRequestedCost: 0,
      newRequestedQuantity: 0,
      manualCreated: true,
      approvedByPM: isProjectManager,
    })
    // Force a state update to recalculate projectExecutionData and assign originalIndex
    setJumpToLastPageTrigger((prev) => prev + 1)
  }

  const handleAutoFillQty = () => {
    projectExecutionGCMaterialDetails.forEach((row, rowIndex) => {
      if (subSystemFilter !== null && `${row?.subSystemIndex}` !== `${subSystemFilter}`) return

      const totalRequestedQuantity = row?.totalRequestedQuantity ?? 0
      const quantity = row?.quantity ?? 0

      const autoFilledQuantity = quantity > totalRequestedQuantity ? quantity - totalRequestedQuantity : 0

      setValue(`sourcing.projectExecutionGCMaterialDetails.${rowIndex}.newRequestedQuantity`, autoFilledQuantity, {
        shouldDirty: true,
      })
    })
  }

  const handleImportExcel = async (e) => {
    const file = e?.target?.files?.[0]

    if (!file) {
      return
    }

    if (subSystemFilter === null) {
      toast.error('Please select Sub-System before importing')
      return
    }

    try {
      const fileStream = await file.arrayBuffer()
      const parsedData = readExcelFile(fileStream)

      const originalData = [...(getValues(`sourcing.projectExecutionGCMaterialDetails`) ?? [])]

      const updatedModelNumbers = new Set()

      // Pre-group uploaded rows by key (modelNumber)
      const groupedRows = new Map()
      parsedData.forEach((row) => {
        const key = row[ACCESSOR_TO_HEADER.modelNumber] ?? ''

        if (groupedRows.has(key)) {
          groupedRows.get(key).rows.push(row)
        } else {
          groupedRows.set(key, { modelNumber: key, rows: [row] })
        }
      })

      for (const { modelNumber, rows } of groupedRows.values()) {
        const isMultiple = rows.length > 1
        const totalQuantity = rows.reduce(
          (sum, row) => sum + (parseInt(row[ACCESSOR_TO_HEADER.newRequestedQuantity]) || 0),
          0
        )
        // Use the last row for non-quantity fields
        const lastRow = rows[rows.length - 1]
        const newRequestedQuantity = isMultiple
          ? totalQuantity
          : parseInt(lastRow[ACCESSOR_TO_HEADER.newRequestedQuantity]) || 0

        const matchingIndex = originalData.findIndex(
          (data) => `${data.subSystemIndex}` === `${subSystemFilter}` && data.modelNumber === modelNumber
        )

        if (matchingIndex !== -1) {
          const matchingRow = originalData[matchingIndex]

          if (matchingRow.manualCreated) {
            // Update description, manualUnitCost and newRequestedQuantity
            const modelSearchResult = await searchGCMaterialsByModelNumber(modelNumber)

            const mappedMasterGC = modelSearchResult?.mappedDetail

            originalData[matchingIndex] = {
              ...matchingRow,
              description: lastRow[ACCESSOR_TO_HEADER.description] ?? matchingRow.description,
              manualUnitCost:
                parseFloat(lastRow[ACCESSOR_TO_HEADER.manualUnitCost]?.toString().replace(/,/g, '')) ||
                matchingRow.manualUnitCost,
              newRequestedQuantity,
              detail: mappedMasterGC,
              allDetails: modelSearchResult?.allDetails ?? [],
            }
          } else {
            // Only update newRequestedQuantity
            originalData[matchingIndex] = {
              ...matchingRow,
              newRequestedQuantity,
            }
          }
        } else {
          // Create new row
          const modelSearchResult = await searchGCMaterialsByModelNumber(modelNumber)

          const { mappedDetail, allDetails } = modelSearchResult

          originalData.push({
            subSystemIndex: subSystemFilter,
            modelNumber,
            description: lastRow[ACCESSOR_TO_HEADER.description] ?? '',
            quantity: parseInt(lastRow[ACCESSOR_TO_HEADER.quantity]) || 0,
            currency: lastRow[ACCESSOR_TO_HEADER.currency] ?? '',
            manualUnitCost: parseFloat(lastRow[ACCESSOR_TO_HEADER.manualUnitCost]?.toString().replace(/,/g, '')) || 0,
            newRequestedQuantity,
            totalRequestedQuantity: 0,
            totalRequestedCost: 0,
            manualCreated: true,
            detail: mappedDetail,
            allDetails: allDetails,
          })
        }
      }

      setValue(`sourcing.projectExecutionGCMaterialDetails`, originalData, { shouldDirty: true })
      // Clear the file input
      if (importExcelRef.current) {
        importExcelRef.current.value = ''
      }
    } catch (error) {
      console.error('error', error)
      toast.error('Failed to import excel file: ' + error?.message)
    }
  }

  const handleDownload = async (data) => {
    const formattedData = formatData(data)
    const accessorData = formattedData.map((row) => {
      return Object.entries(row).reduce((normalizedRow, [key, value]) => {
        const accessorKey = HEADER_TO_ACCESSOR[key] || key
        normalizedRow[accessorKey] = value
        return normalizedRow
      }, {})
    })

    const optionConfigs = [
      {
        options: subSystemOptions.map((option) => option.label),
        columnKey: 'D',
      },
      {
        options: currencyOptions.map((option) => option.label),
        columnKey: 'H',
      },
    ]

    const excelBlob = await createExcelWithOptions(ACCESSOR_TO_HEADER, accessorData, optionConfigs)
    saveAs(excelBlob, 'E-Approval GC Material Project Execution.xlsx')
  }

  return (
    <Flex direction={'column'} gap="2">
      {subSystemFilter !== null && (
        <Flex gap="2">
          <Spacer />
          <input
            type="file"
            ref={importExcelRef}
            accept={excelFileTypes}
            disabled={disabled}
            onChange={handleImportExcel}
          />
          <Button colorScheme="blue" onClick={handleAddItem} isDisabled={disabled}>
            +
          </Button>
          <Button colorScheme="teal" onClick={handleAutoFillQty} isDisabled={disabled}>
            Auto Fill Qty
          </Button>
        </Flex>
      )}

      <Flex overflowX={'scroll'} direction={'column'}>
        <ClientSideTable
          tableWidth="100%"
          columnDef={columnDef}
          data={projectExecutionData}
          jumpToLastPageTrigger={jumpToLastPageTrigger}
          disabled={disabled || subSystemFilter === null}
          canDownload={subSystemFilter !== null}
          onDownload={handleDownload}
          updateData={handleUpdateTableData}
        />
      </Flex>

      <Flex direction={'row'} gap="1" justifyContent={'end'} py="2">
        <Flex gap="4" align="center">
          <Text fontWeight="semibold" minW="200px" textAlign="right">
            PM100 Cost(HKD):
          </Text>
          <Text minW="140px" textAlign="right">
            ${numberWithCommas(pm100TotalCost)}
          </Text>
        </Flex>
        <Flex gap="4" align="center">
          <Text fontWeight="semibold" minW="200px" textAlign="right">
            Current Cost(HKD):
          </Text>
          <Text minW="140px" textAlign="right">
            ${numberWithCommas(currentCost)}
          </Text>
        </Flex>
        <Flex gap="4" align="center">
          <Text fontWeight="semibold" minW="200px" textAlign="right">
            Remaining(HKD):
          </Text>
          <Text minW="140px" textAlign="right" color={pm100TotalCost - currentCost < 0 ? 'red.500' : 'inherit'}>
            ${numberWithCommas(pm100TotalCost - currentCost)}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  )
}

export default OfferGCMaterialProjectExecutionTable
